import Container from '@ui/container/container';
import AddEngagementCard from './add-engagement-card';
import { useWorkingSession } from '@/utils/use-working-session';
import { useAppSelector } from '@/store/hooks';
import {
  getPublishedStatus,
  getWorkingProjectUuid,
  selectSessionEngagementModules,
  selectSessionEngagementTotals,
  selectWorkingProjectDefaultLanguage,
} from '@/store/slices/project/selectors';
import {
  EngagementDetails,
  PageModule,
  PageModuleType,
  QuestionPrompt,
  Survey,
  SurveyType,
} from '@/types/module.types';
import { ICONS } from '@ui/icons/types';
import { useLoadSessionEngagementTotals } from '@/connection/hooks/use-load-engagement-totals';
import CustomCarousel from '@ui/custom-carousel/custom-carousel';

import Link from '@ui/links/link';
import { getBaseAdminURL } from '@/utils/urls';
import useLanguageParams from '@/utils/use-language-params';
import { Activity } from 'react';
import { selectBodyAttributesTheme } from '@/store/slices/layout-grid/selectors';
import { EColorVariablesV2, EPaletteModes } from '@/types/theme-packs.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import EngagementCard from './engagement-card';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EProjectStatus } from '@/types/projects.types';

const Engagement = () => {
  const session = useWorkingSession();
  const engagementModules = useAppSelector(selectSessionEngagementModules(session?.uuid));
  const sessionEngagementTotals = useAppSelector(selectSessionEngagementTotals);
  const mode = useAppSelector(selectBodyAttributesTheme);
  const workingProjectUuid = useAppSelector(getWorkingProjectUuid);
  const paramsLanguage = useLanguageParams();
  const _defaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const publishedStatus = useAppSelector(getPublishedStatus);

  const language = paramsLanguage || _defaultLanguage || 'en';
  const sessionEngagementTotalsMap = new Map(
    sessionEngagementTotals?.map((total) => [total.module_id, total.responses])
  );
  const isDarkMode = mode === EPaletteModes.Dark;
  const baseUrl = `${getBaseAdminURL()}/admin/events/create/${workingProjectUuid}/session/${session?.uuid}/${language}/content/tabs/engage`;

  useLoadSessionEngagementTotals(session?.uuid);

  const getEngagementDetails = (pageModule: PageModule) => {
    const engagementDetails: EngagementDetails = {
      id: pageModule.id || 0,
      countBg: isDarkMode ? 'var(--global-opacity-white-10)' : 'var(--global-opacity-black-10)',
      labelFontColor: 'var(--true-white)', // label will always have white text for contrast agains color pill
    };

    const subPath = publishedStatus?.status === EProjectStatus.PUBLIC ? 'responses' : 'create';

    if (pageModule.type === PageModuleType.survey) {
      const engagementItem: Survey = pageModule.modules?.[0];
      engagementDetails.title = engagementItem.name.base;

      // includes Quiz, Poll, and Survey engagement types
      if (engagementItem.survey_type === SurveyType.quiz) {
        // Quiz
        engagementDetails.icon = ICONS.QUIZ;
        engagementDetails.label = 'Quiz';
        engagementDetails.linkPartial = `/quizzes/${pageModule.id}/${subPath}`;
        engagementDetails.color = '--primitives-mode-1-violet-500';
        engagementDetails.gradientBg = isDarkMode
          ? 'linear-gradient(to left, rgba(41, 41, 75, 0.6), rgba(41, 41, 75, 0.2))'
          : 'linear-gradient(to left, rgba(102, 99, 249, 0.4), rgba(102, 99, 249, 0.2))';
      } else if (engagementItem.show_results) {
        // Poll
        engagementDetails.icon = ICONS.POLL_FILLED;
        engagementDetails.label = 'Poll';
        engagementDetails.linkPartial = `/surveys/${pageModule.id}/${subPath}`;
        engagementDetails.color = '--primitives-mode-1-green-800';
        engagementDetails.gradientBg = isDarkMode
          ? 'linear-gradient(to left, rgba(31, 64, 40, 0.6), rgba(31, 64, 40, 0.2))'
          : 'linear-gradient(to left, rgba(32, 101, 49, 0.4), rgba(32, 101, 49, 0.2))';
      } else {
        // Survey
        engagementDetails.icon = ICONS.SURVEY;
        engagementDetails.label = 'Survey';
        engagementDetails.linkPartial = `/surveys/${pageModule.id}/${subPath}`;
        engagementDetails.color = '--primitives-mode-1-blue-500';
        engagementDetails.gradientBg = isDarkMode
          ? 'linear-gradient(to left, rgba(32, 41, 76, 0.6), rgba(32, 41, 76, 0.2))'
          : 'linear-gradient(to left, rgba(54, 101, 255, 0.3), rgba(54, 101, 255, 0.1))';
      }
    } else if (pageModule.type === PageModuleType.questions) {
      const engagementItem: QuestionPrompt = pageModule.modules?.[0];

      // Question engagement type
      engagementDetails.icon = ICONS.QUESTION_FILLED;
      engagementDetails.label = 'Q&A';
      engagementDetails.title = engagementItem.title.base;
      engagementDetails.linkPartial = `/q-and-a/${pageModule.id}/${subPath}`;
      engagementDetails.color = '--primitives-mode-1-yellow-400';
      engagementDetails.labelFontColor = 'var(--true-black)'; // always have Q&A label be black for contrast against yellow
      engagementDetails.gradientBg = isDarkMode
        ? 'linear-gradient(to left, rgba(58, 50, 10, 0.6), rgba(58, 50, 10, 0.2))'
        : 'linear-gradient(to left, rgba(255, 222, 59, 0.4), rgba(255, 222, 59, 0.2))';
    } else if (pageModule.type === PageModuleType.comment_box_prompt) {
      const engagementItem: QuestionPrompt = pageModule.modules?.[0];
      // Comment box engagement type
      engagementDetails.icon = ICONS.COMMENT_FULL;
      engagementDetails.label = 'Comment Box';
      engagementDetails.title = engagementItem.title.base;
      engagementDetails.linkPartial = `/comment-box/${pageModule.id}/${subPath}`;
      engagementDetails.color = '--primitives-mode-1-pink-300';
      engagementDetails.gradientBg = isDarkMode
        ? 'linear-gradient(to left, rgba(72, 31, 60, 0.6), rgba(72, 31, 60, 0.2))'
        : 'linear-gradient(to left, rgba(255, 133, 208, 0.4), rgba(255, 133, 208, 0.2))';
    }

    return engagementDetails;
  };

  const engagementDetails = engagementModules?.map((em) => {
    return getEngagementDetails(em);
  });

  return (
    <Container margin="24px var(--main-content-horizontal-padding)" data-component="Engagement">
      <Container className="flex fd-row jc-space-between">
        <Container className="H5 margin-b-8">Engage</Container>
        <Activity mode={(engagementDetails?.length || 0) > 3 ? 'visible' : 'hidden'}>
          <Link
            className="L2"
            to={baseUrl}
            textColor={getThemeColorContrastCSSVariable(EColorVariablesV2.primary, EColorVariablesV2.background)}
            hoverOpacity={0.8}
          >
            Edit Engage
          </Link>
        </Activity>
      </Container>

      <Container height="140px">
        <OptionalComponent display={(engagementDetails?.length || 0) > 2}>
          <CustomCarousel
            gap="16px"
            virtualized
            list={engagementDetails || []}
            itemContainerDimensions={{ width: '320px', height: '140px' }}
            renderItem={(details) => {
              if (!details) return;

              return (
                <EngagementCard
                  engagementDetails={details}
                  baseUrl={baseUrl}
                  engagementCount={sessionEngagementTotalsMap.get(details.id) || 0}
                />
              );
            }}
          />
        </OptionalComponent>
        <OptionalComponent display={(engagementDetails?.length || 0) <= 2}>
          <Container className="flex fd-row gap-16">
            {engagementDetails?.map((details, index) => (
              <EngagementCard
                key={index}
                engagementDetails={details}
                baseUrl={baseUrl}
                engagementCount={sessionEngagementTotalsMap.get(details.id) || 0}
              />
            ))}
            <AddEngagementCard />
          </Container>
        </OptionalComponent>
      </Container>
    </Container>
  );
};

export default Engagement;
